import { Input } from "@/components/ui/input";
import {
  standardFactor,
  unitFamily,
} from "@/modules/inventory/domain/lib/unit-conversion";
import { FormInput, FormSelect, RHF } from "@/shared/forms";
import { useEffect } from "react";
import { BASE_UNITS } from "../api/client";

const STANDARD = new Set<string>(BASE_UNITS);

const RECEIVE_PACKS = [
  { value: "ກວດ", label: "ກວດ" },
  { value: "ຖົງ", label: "ຖົງ" },
] as const;

function unitOptions(baseUnit: string, current: string) {
  const family = unitFamily(baseUnit || "g");
  const options: { value: string; label: string }[] = BASE_UNITS.filter(
    (unit) => unitFamily(unit) === family,
  ).map((unit) => ({ value: unit, label: unit }));
  if (
    current &&
    !STANDARD.has(current) &&
    !options.some((o) => o.value === current)
  ) {
    options.push({ value: current, label: current });
  }
  return options;
}

function receiveUnitOptions(baseUnit: string, current: string) {
  const options = unitOptions(baseUnit, current);
  for (const pack of RECEIVE_PACKS) {
    if (!options.some((option) => option.value === pack.value)) {
      options.push(pack);
    }
  }
  return options;
}

export function IngredientUnitFields() {
  const baseUnit = (RHF.useWatch({ name: "baseUnit" }) as string) ?? "g";
  const receiveUnit = (RHF.useWatch({ name: "receiveUnit" }) as string) ?? "";
  const receiveFactor = Number(RHF.useWatch({ name: "receiveToBase" }));
  const issueUnit = (RHF.useWatch({ name: "issueUnit" }) as string) ?? "";
  const issueFactor = Number(RHF.useWatch({ name: "issueToBase" }));
  const { setValue } = RHF.useFormContext();

  useEffect(() => {
    syncStandardUnit(
      baseUnit,
      receiveUnit,
      receiveFactor,
      "receiveUnit",
      "receiveToBase",
      setValue,
    );
    syncStandardUnit(
      baseUnit,
      issueUnit,
      issueFactor,
      "issueUnit",
      "issueToBase",
      setValue,
    );
  }, [baseUnit, receiveUnit, receiveFactor, issueUnit, issueFactor, setValue]);

  const receiveLocked = STANDARD.has(receiveUnit);
  const issueLocked = STANDARD.has(issueUnit);

  return (
    <div className="space-y-4 rounded-[12px] border bg-muted/20 p-4">
      <p className="font-medium text-sm">ຫນ່ວຍຮັບເຂົ້າ ແລະ ຫນ່ວຍຕັດຂາຍ</p>
      <p className="text-muted-foreground text-xs">
        ຄັງເກັບເປັນຫນ່ວຍພື້ນຖານ. ຮັບເຂົ້າແລະສູດໃຊ້ຫນ່ວຍນີ້ ແລ້ວຄູນອັດຕາເຂົ້າຄັງ.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormSelect
          name="receiveUnit"
          label="ຫນ່ວຍຮັບເຂົ້າ"
          options={receiveUnitOptions(baseUnit, receiveUnit)}
          requiredMark
        />
        <FormInput
          name="receiveToBase"
          label={factorLabel(receiveUnit, baseUnit, "ຫນ່ວຍຮັບ")}
          type="number"
          step="any"
          min={0}
          disabled={receiveLocked}
          requiredMark
        />
        <FormSelect
          name="issueUnit"
          label="ຫນ່ວຍຕັດຂາຍ"
          options={unitOptions(baseUnit, issueUnit)}
          requiredMark
        />
        <FormInput
          name="issueToBase"
          label={factorLabel(issueUnit, baseUnit, "ຫນ່ວຍຕັດ")}
          type="number"
          step="any"
          min={0}
          disabled={issueLocked}
          requiredMark
        />
      </div>
      <PackInput
        label="ຫນ່ວຍແພັກຮັບເຂົ້າອື່ນ"
        current={receiveUnit}
        onPack={(next) => {
          setValue("receiveUnit", next);
          if (!(receiveFactor > 0)) setValue("receiveToBase", 1);
        }}
      />
      <PackInput
        label="ຫນ່ວຍແພັກຕັດຂາຍ"
        current={issueUnit}
        onPack={(next) => {
          setValue("issueUnit", next);
          if (!(issueFactor > 0)) setValue("issueToBase", 1);
        }}
      />
      <p className="text-sm">
        ຮັບ 1 {receiveUnit || "—"} ={" "}
        {Number.isFinite(receiveFactor) ? receiveFactor : "—"} {baseUnit} ໃນຄັງ ·
        ຕັດ 1 {issueUnit || "—"} ={" "}
        {Number.isFinite(issueFactor) ? issueFactor : "—"} {baseUnit}
      </p>
    </div>
  );
}

function factorLabel(unit: string, baseUnit: string, fallback: string): string {
  const selected = unit.trim() || fallback;
  const base = baseUnit.trim() || "ຫນ່ວຍພື້ນຖານ";
  return `1 ${selected} = ເທົ່າໃດ ${base}`;
}

function syncStandardUnit(
  baseUnit: string,
  unit: string,
  factor: number,
  unitName: string,
  factorName: string,
  setValue: (name: string, value: unknown) => void,
) {
  if (!unit || unitFamily(unit) === "pack") return;
  const fixed = standardFactor(baseUnit, unit);
  if (fixed == null) {
    setValue(unitName, baseUnit);
    setValue(factorName, 1);
    return;
  }
  if (factor !== fixed) setValue(factorName, fixed);
}

function PackInput({
  label,
  current,
  onPack,
}: {
  label: string;
  current: string;
  onPack: (value: string) => void;
}) {
  const isPack = !!current && !STANDARD.has(current);
  const inputId = label.replace(/\s+/g, "-");
  return (
    <div className="space-y-1 text-sm">
      <label htmlFor={inputId} className="text-muted-foreground">
        {label}
      </label>
      <Input
        id={inputId}
        value={isPack ? current : ""}
        placeholder="ປະໄວ້ວ່າງຖ້າໃຊ້ຫນ່ວຍມາດຕະຖານ"
        onChange={(e) => {
          const next = e.target.value.trim();
          if (!next) return;
          const lower = next.toLowerCase();
          onPack(STANDARD.has(lower) ? lower : next);
        }}
      />
    </div>
  );
}
